import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";
import { resolveAvatarUrl, handleAvatarError } from "../utils/profilePicUrl";
import { downloadApiFile } from "../utils/downloadFile";
import {
  isEmployer,
  isAdmin,
  applicationStatusLabel,
  APPLICATION_STATUS_LABELS,
} from "../utils/userRole";

const API_URL = getApiBaseUrl();

const STATUS_ACTIONS = [
  { status: "under_review", label: "Under Review" },
  { status: "shortlisted", label: "Shortlist" },
  { status: "interview_scheduled", label: "Schedule Interview" },
  { status: "offered", label: "Offer" },
  { status: "hired", label: "Hire" },
  { status: "rejected", label: "Reject" },
];

const chip = (active) => ({
  padding: "6px 12px",
  borderRadius: 999,
  border: active ? "1px solid #0f766e" : "1px solid #cbd5e1",
  background: active ? "#ccfbf1" : "#fff",
  color: active ? "#0f766e" : "#475569",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
});

const btn = {
  padding: "7px 12px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  background: "#fff",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  color: "#0f172a",
};

/**
 * Employer dashboard: review job applications.
 */
export default function EmployerApplications({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const postIdFilter = searchParams.get("post_id") || "";
  const highlightId = searchParams.get("application_id") || "";

  const [apps, setApps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [interviewAt, setInterviewAt] = useState("");
  const [actionBusy, setActionBusy] = useState(false);

  const employerId = user?.id;

  const load = useCallback(async () => {
    if (!employerId) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        employer_id: String(employerId),
        limit: "150",
      });
      if (postIdFilter) params.set("post_id", postIdFilter);
      if (statusFilter && statusFilter !== "all") {
        params.set("status", statusFilter);
      }
      if (query.trim()) params.set("q", query.trim());

      const res = await axios.get(
        `${API_URL}/get_job_applications.php?${params.toString()}`
      );
      if (!res.data?.success) {
        setError(res.data?.message || "Failed to load applications.");
        setApps([]);
        return;
      }
      setApps(res.data.applications || []);
      setSummary(res.data.summary || null);
    } catch (err) {
      setError(err?.message || "Network error.");
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, [employerId, postIdFilter, statusFilter, query]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!highlightId || !apps.length) return;
    const found = apps.find((a) => String(a.id) === String(highlightId));
    if (found) setSelected(found);
  }, [highlightId, apps]);

  const filters = useMemo(
    () => [
      { key: "all", label: `All (${summary?.total ?? 0})` },
      { key: "submitted", label: "Submitted" },
      { key: "under_review", label: "Under Review" },
      { key: "shortlisted", label: `Shortlisted (${summary?.shortlisted ?? 0})` },
      {
        key: "interview_scheduled",
        label: "Interview",
      },
      { key: "hired", label: `Hired (${summary?.hired ?? 0})` },
      { key: "rejected", label: "Rejected" },
    ],
    [summary]
  );

  if (!user) {
    return <NavigateHint />;
  }

  if (!isEmployer(user) && !isAdmin(user)) {
    return (
      <div style={{ maxWidth: 640, margin: "40px auto", padding: 20, textAlign: "center" }}>
        <p>Only employer and admin accounts can manage job applications.</p>
        <Link to="/my-applications">Go to My Applications</Link>
      </div>
    );
  }

  const updateStatus = async (application, status) => {
    if (status === "interview_scheduled" && !interviewAt) {
      alert("Pick an interview date/time first.");
      return;
    }
    setActionBusy(true);
    try {
      const payload = {
        application_id: application.id,
        employer_id: employerId,
        status,
      };
      if (status === "interview_scheduled") {
        payload.interview_at = interviewAt;
      }
      const res = await axios.post(
        `${API_URL}/update_application_status.php`,
        payload
      );
      if (!res.data?.success) {
        alert(res.data?.message || "Update failed.");
        return;
      }
      const updated = res.data.application;
      setApps((prev) =>
        prev.map((a) =>
          a.id === application.id
            ? {
                ...a,
                ...updated,
                applicant: a.applicant,
                job_title: a.job_title,
              }
            : a
        )
      );
      setSelected((prev) =>
        prev && prev.id === application.id
          ? { ...prev, ...updated, applicant: prev.applicant, job_title: prev.job_title }
          : prev
      );
      // soft refresh summary
      load();
    } catch (err) {
      alert(err?.message || "Network error.");
    } finally {
      setActionBusy(false);
    }
  };

  const markViewed = async (application) => {
    if (application.status !== "submitted") return;
    await updateStatus(application, "viewed");
  };

  const downloadResume = async (application) => {
    const url = `${API_URL}/download_resume.php?application_id=${application.id}&user_id=${employerId}`;
    try {
      await downloadApiFile(url, { fallbackName: "resume", errorMessage: "Could not download the resume." });
      markViewed(application);
    } catch (err) {
      alert(err.message);
    }
  };

  const messageApplicant = (application) => {
    window.dispatchEvent(
      new CustomEvent("cool:open-chat", {
        detail: { userId: application.applicant_id },
      })
    );
  };

  const openDetail = (application) => {
    setSelected(application);
    markViewed(application);
    const next = new URLSearchParams(searchParams);
    next.set("application_id", String(application.id));
    setSearchParams(next, { replace: true });
  };

  const deleteApplication = async (application) => {
    if (!application?.id) return;
    const name = application.full_name || "this applicant";
    const job = application.job_title || "this job";
    const ok = window.confirm(
      `Delete application from ${name} for “${job}”? This cannot be undone.`
    );
    if (!ok) return;

    setActionBusy(true);
    try {
      const res = await axios.post(`${API_URL}/delete_job_application.php`, {
        application_id: application.id,
        employer_id: employerId,
        job_title: application.job_title || "",
      });
      if (!res.data?.success) {
        alert(res.data?.message || "Delete failed.");
        return;
      }
      setApps((prev) => prev.filter((a) => a.id !== application.id));
      setSelected((prev) => (prev && prev.id === application.id ? null : prev));
      const next = new URLSearchParams(searchParams);
      if (String(next.get("application_id")) === String(application.id)) {
        next.delete("application_id");
        setSearchParams(next, { replace: true });
      }
      load();
    } catch (err) {
      alert(err?.message || "Network error.");
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: 1100,
        margin: "0 auto",
        padding: "24px 16px 64px",
        minHeight: "100vh",
        background: "#f8fafc",
      }}
    >
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          border: "none",
          background: "transparent",
          color: "#0f766e",
          cursor: "pointer",
          marginBottom: 12,
          padding: 0,
        }}
      >
        ← Back
      </button>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 18,
        }}
      >
        <div>
          <h1 style={{ margin: "0 0 6px", fontSize: 28, color: "#0f172a" }}>
            Job Applications
          </h1>
          <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>
            {isAdmin(user)
              ? "Admin view: all hiring applications across every employer."
              : "Review applicants, download resumes, and manage hiring status."}
          </p>
        </div>
        {summary && (
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              fontSize: 13,
            }}
          >
            <Stat label="Applicants" value={summary.total} />
            <Stat label="Shortlisted" value={summary.shortlisted} />
            <Stat label="Hired" value={summary.hired} />
            <Stat label="Open jobs" value={summary.open_jobs} />
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 14,
          alignItems: "center",
        }}
      >
        {filters.map((f) => (
          <button
            key={f.key}
            type="button"
            style={chip(statusFilter === f.key)}
            onClick={() => setStatusFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, email, skills…"
          style={{
            marginLeft: "auto",
            padding: "8px 12px",
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            minWidth: 200,
            fontSize: 13,
          }}
        />
        {postIdFilter && (
          <button
            type="button"
            style={chip(false)}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("post_id");
              setSearchParams(next);
            }}
          >
            Clear job filter
          </button>
        )}
      </div>

      {error && (
        <div style={{ color: "#b91c1c", marginBottom: 12 }}>{error}</div>
      )}

      {loading ? (
        <p style={{ color: "#64748b" }}>Loading applications…</p>
      ) : apps.length === 0 ? (
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: 32,
            textAlign: "center",
            color: "#64748b",
          }}
        >
          {summary?.total > 0
            ? "No applications match this filter."
            : "No applications yet."}
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: selected ? "1fr 1.1fr" : "1fr",
            gap: 16,
            alignItems: "start",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {apps.map((a) => {
              const active = selected && selected.id === a.id;
              return (
                <div
                  key={a.id}
                  style={{
                    border: active
                      ? "1px solid #0f766e"
                      : "1px solid #e2e8f0",
                    background: "#fff",
                    borderRadius: 12,
                    padding: 14,
                    display: "flex",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => openDetail(a)}
                    style={{
                      textAlign: "left",
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      cursor: "pointer",
                      display: "flex",
                      gap: 12,
                      alignItems: "center",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <img
                      src={resolveAvatarUrl(a.applicant)}
                      onError={handleAvatarError}
                      alt=""
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>
                        {a.full_name}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: a.job_missing ? "#b45309" : "#64748b",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {a.job_title}
                      </div>
                      <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                        {a.created_at
                          ? new Date(a.created_at).toLocaleString()
                          : ""}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "3px 8px",
                        borderRadius: 6,
                        background: "#f1f5f9",
                        color: "#0f766e",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {applicationStatusLabel(a.status)}
                    </span>
                  </button>
                  <button
                    type="button"
                    disabled={actionBusy}
                    title="Delete application"
                    aria-label={`Delete application from ${a.full_name || "applicant"}`}
                    onClick={() => deleteApplication(a)}
                    style={{
                      ...btn,
                      color: "#b91c1c",
                      borderColor: "#fecaca",
                      background: "#fff5f5",
                      padding: "6px 10px",
                      flexShrink: 0,
                    }}
                  >
                    Delete
                  </button>
                </div>
              );
            })}
          </div>

          {selected && (
            <div
              style={{
                background: "#fff",
                borderRadius: 14,
                border: "1px solid #e2e8f0",
                padding: 20,
                position: "sticky",
                top: 72,
              }}
            >
              <div style={{ display: "flex", gap: 14, marginBottom: 16 }}>
                <img
                  src={resolveAvatarUrl(selected.applicant)}
                  onError={handleAvatarError}
                  alt=""
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
                <div>
                  <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>
                    {selected.full_name}
                  </h2>
                  <div style={{ fontSize: 13, color: "#64748b" }}>
                    {selected.job_title}
                  </div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>
                    Status:{" "}
                    <strong>{applicationStatusLabel(selected.status)}</strong>
                  </div>
                </div>
              </div>

              <div style={{ fontSize: 14, color: "#334155", lineHeight: 1.6 }}>
                <p style={{ margin: "0 0 6px" }}>
                  <strong>Email:</strong> {selected.email}
                </p>
                <p style={{ margin: "0 0 6px" }}>
                  <strong>Mobile:</strong> {selected.mobile || "—"}
                </p>
                <p style={{ margin: "0 0 6px" }}>
                  <strong>Applied:</strong>{" "}
                  {selected.created_at
                    ? new Date(selected.created_at).toLocaleString()
                    : "—"}
                </p>
                {selected.portfolio_url && (
                  <p style={{ margin: "0 0 6px" }}>
                    <strong>Portfolio:</strong>{" "}
                    <a
                      href={selected.portfolio_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selected.portfolio_url}
                    </a>
                  </p>
                )}
                {selected.interview_at && (
                  <p style={{ margin: "0 0 6px" }}>
                    <strong>Interview:</strong>{" "}
                    {new Date(selected.interview_at).toLocaleString()}
                  </p>
                )}
              </div>

              {selected.cover_letter && (
                <div style={{ marginTop: 14 }}>
                  <h3 style={{ fontSize: 14, margin: "0 0 6px" }}>Cover letter</h3>
                  <div
                    style={{
                      background: "#f8fafc",
                      borderRadius: 10,
                      padding: 12,
                      fontSize: 14,
                      whiteSpace: "pre-wrap",
                      color: "#334155",
                    }}
                  >
                    {selected.cover_letter}
                  </div>
                </div>
              )}

              {selected.applicant?.bio && (
                <div style={{ marginTop: 14 }}>
                  <h3 style={{ fontSize: 14, margin: "0 0 6px" }}>About</h3>
                  <p style={{ margin: 0, fontSize: 14, color: "#475569" }}>
                    {selected.applicant.bio}
                  </p>
                </div>
              )}

              {!!selected.applicant?.skills?.length && (
                <div style={{ marginTop: 14 }}>
                  <h3 style={{ fontSize: 14, margin: "0 0 8px" }}>Skills</h3>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {selected.applicant.skills.map((s) => (
                      <span
                        key={s.id || s.skill_name}
                        style={{
                          fontSize: 12,
                          padding: "4px 8px",
                          borderRadius: 6,
                          background: "#ecfdf5",
                          color: "#0f766e",
                        }}
                      >
                        {s.skill_name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {!!selected.applicant?.experience?.length && (
                <div style={{ marginTop: 14 }}>
                  <h3 style={{ fontSize: 14, margin: "0 0 8px" }}>Experience</h3>
                  {selected.applicant.experience.map((ex) => (
                    <div
                      key={ex.id}
                      style={{
                        marginBottom: 10,
                        fontSize: 13,
                        color: "#334155",
                      }}
                    >
                      <strong>{ex.job_title}</strong> · {ex.company}
                      <div style={{ color: "#94a3b8", fontSize: 12 }}>
                        {ex.start_date || "?"} –{" "}
                        {ex.is_current ? "Present" : ex.end_date || "?"}
                      </div>
                      {ex.description && (
                        <div style={{ marginTop: 4 }}>{ex.description}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 18,
                }}
              >
                <Link
                  to={`/profile/${selected.applicant_id}`}
                  style={{ ...btn, textDecoration: "none" }}
                >
                  View Profile
                </Link>
                {selected.has_resume && (
                  <button
                    type="button"
                    style={btn}
                    onClick={() => downloadResume(selected)}
                  >
                    Download Resume
                  </button>
                )}
                <button
                  type="button"
                  style={btn}
                  onClick={() => messageApplicant(selected)}
                >
                  Message Applicant
                </button>
                <button
                  type="button"
                  disabled={actionBusy}
                  style={{
                    ...btn,
                    color: "#b91c1c",
                    borderColor: "#fecaca",
                    background: "#fff5f5",
                  }}
                  onClick={() => deleteApplication(selected)}
                >
                  Delete Application
                </button>
              </div>

              <div style={{ marginTop: 18 }}>
                <h3 style={{ fontSize: 14, margin: "0 0 8px" }}>Update status</h3>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    color: "#64748b",
                    marginBottom: 6,
                  }}
                >
                  Interview date/time (for Schedule Interview)
                </label>
                <input
                  type="datetime-local"
                  value={interviewAt}
                  onChange={(e) => setInterviewAt(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    marginBottom: 10,
                    boxSizing: "border-box",
                  }}
                />
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {STATUS_ACTIONS.map((a) => (
                    <button
                      key={a.status}
                      type="button"
                      disabled={actionBusy}
                      style={{
                        ...btn,
                        background:
                          a.status === "hired"
                            ? "#0f766e"
                            : a.status === "rejected"
                              ? "#fff"
                              : "#fff",
                        color:
                          a.status === "hired"
                            ? "#fff"
                            : a.status === "rejected"
                              ? "#b91c1c"
                              : "#0f172a",
                        borderColor:
                          a.status === "rejected" ? "#fecaca" : "#cbd5e1",
                      }}
                      onClick={() => updateStatus(selected, a.status)}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>
                  Statuses:{" "}
                  {Object.values(APPLICATION_STATUS_LABELS)
                    .filter((l) => l !== "Withdrawn")
                    .join(" · ")}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 10,
        padding: "8px 14px",
        minWidth: 88,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
        {value ?? 0}
      </div>
      <div style={{ fontSize: 11, color: "#64748b" }}>{label}</div>
    </div>
  );
}

function NavigateHint() {
  return (
    <div style={{ padding: 40, textAlign: "center" }}>
      Please log in to view applications.
    </div>
  );
}
